import { BareInput } from "components/DesignSystem/Input/Input";
import { Button } from "components/DesignSystem/Button/Button";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import DropDown from "components/DesignSystem/Dropdown/Dropdown";
import { Form, Formik } from "formik";
import { MouseEvent, useRef, useState } from "react";
import ConditionalToolTip from "components/design/conditionalToolTip";

type Props = {
  title: string;
  fieldName: string;
  initialValue: string;
  placeholder: string;
  isLoading: boolean;
  onSave: (value: string) => Promise<void>;
};

const stopPropagation = (e: MouseEvent) => e.stopPropagation();

export const TextAreaEditDropdown = ({
  title,
  fieldName,
  initialValue,
  placeholder,
  isLoading,
  onSave,
}: Props) => {
  const [open, setOpen] = useState(false);
  const valuesRef = useRef<Record<string, string>>({
    [fieldName]: initialValue,
  });
  const cancelledRef = useRef(false);

  const handleOpenChange = async (next: boolean) => {
    if (
      !next &&
      !cancelledRef.current &&
      valuesRef.current[fieldName] !== initialValue
    ) {
      await onSave(valuesRef.current[fieldName]);
    }
    cancelledRef.current = false;
    setOpen(next);
  };

  const handleSubmit = async (values: Record<string, string>) => {
    await onSave(values[fieldName]);
    setOpen(false);
  };

  return (
    <DropDown.Root open={open} onOpenChange={handleOpenChange} modal={false}>
      <DropDown.Trigger asChild onClick={stopPropagation}>
        <div>
          <ConditionalToolTip
            condition={
              initialValue && (
                <div className="t-text-center t-break-words t-hyphen-auto t-max-w-[500px]">
                  {initialValue}
                </div>
              )
            }
          >
            <BareInput value={initialValue} placeholder="-" readOnly />
          </ConditionalToolTip>
        </div>
      </DropDown.Trigger>
      <DropDown.Portal>
        <DropDown.Content
          side="top"
          align="start"
          onClick={stopPropagation}
          className="t-w-96 t-rounded-lg t-bg-surface t-p-4 t-drop-shadow-dropdown"
        >
          <Formik
            initialValues={{ [fieldName]: initialValue }}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ resetForm, values }) => {
              valuesRef.current = values;

              const handleCancel = () => {
                cancelledRef.current = true;
                resetForm();
                setOpen(false);
              };

              return (
                <Form>
                  <div className="t-mb-3 t-flex t-items-center t-justify-between">
                    <span className="t-text-body-lg t-font-semibold t-text-text-100">
                      {title}
                    </span>
                    <Button
                      customType="ghost_icon"
                      size="small"
                      type="button"
                      onClick={handleCancel}
                    >
                      ✕
                    </Button>
                  </div>
                  <TextArea
                    name={fieldName}
                    placeholder={placeholder}
                    rows={6}
                    block
                    autoResize={false}
                  />
                  <div className="t-mt-3 t-flex t-justify-end t-gap-2">
                    <Button size="small" type="button" onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      type="submit"
                      customType="primary"
                      isLoading={isLoading}
                    >
                      Save
                    </Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </DropDown.Content>
      </DropDown.Portal>
    </DropDown.Root>
  );
};
